import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import QueryString from "qs";

const DefaultConfig = {
  paramsSerializer: (params: object) => {
    return QueryString.stringify(params, { arrayFormat: "repeat" });
  },
};

function getCookie(name: string) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

export class APIRequest {
  config: AxiosRequestConfig;
  instance: AxiosInstance;

  constructor(options: AxiosRequestConfig) {
    this.config = options;
    this.instance = axios.create(options);
  }

  request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    return new Promise((resolve, reject) => {
      const accessToken: string = getCookie("Authorization") ?? "";
      if (accessToken) {
        config.headers = {
          ...config.headers,
          client: "warehouse-web",
          Authorization: `Bearer ${accessToken}`,
        };
      }

      this.instance
        .request<unknown, AxiosResponse<T, unknown>>({
          ...DefaultConfig,
          ...config,
        })
        .then((res) => {
          resolve(res.data);
        })
        .catch(async function (err) {
          let res = {
            success: false,
            message: err?.msg ?? err?.toString() ?? "",
            status: err?.status,
          };

          const response = err?.response;
          const data = response?.data;
          const ct = response?.headers?.["content-type"] || "";

          if (
            response?.config?.responseType === "blob" &&
            data instanceof Blob
          ) {
            const text = await data.text();
            let payload: any;
            try {
              payload = ct.includes("application/json")
                ? JSON.parse(text)
                : { message: text };
            } catch {
              payload = { message: text };
            }

            err.response.data = payload;
            err.message = payload.message ?? payload.detail ?? err.message;
          }

          const requestUrl = err.request.responseURL;
          console.error("API Error: failed to call ", requestUrl, err.message);
          res = {
            ...res,
            ...(err?.response?.data ?? {}),
          };
          reject(res);
        });
    });
  }

  get<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: "GET", withCredentials: true });
  }

  post<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: "POST", withCredentials: true });
  }

  delete<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: "DELETE" });
  }

  patch<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: "PATCH" });
  }

  put<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: "PUT" });
  }
}
